import React, { useState, useEffect } from 'react';
import { AlertTriangle, Clock, Calendar, AlertCircle } from 'lucide-react';
import { CarpoolVehicle } from '../types';
import baserowApi from '../services/baserowApi';

interface NotificationBarProps {
  vehicles: CarpoolVehicle[];
}

interface DamageReport {
  id: number;
  title: string;
  description?: string;
  status: { id: number; value: string; color: string };
  carpool: Array<{ id: number; value: string }>;
}

// Kategorien für Projekte
const CATEGORY_OPTIONS = [
  'Schaden',
  'Termin', 
  'Service & Inspektion',
] as const;
type ProjectCategory = typeof CATEGORY_OPTIONS[number];

function extractCategoryFromDescription(description: string): ProjectCategory | null {
  if (!description) return null;
  const match = description.match(/^Kategorie:\s*(.+)$/m);
  if (match) {
    const value = match[1].trim();
    if ((CATEGORY_OPTIONS as readonly string[]).includes(value)) {
      return value as ProjectCategory;
    }
  }
  return null;
}

interface Notification {
  type: 'warning' | 'error' | 'info';
  message: string;
  count: number;
  icon: React.ReactNode;
}

const NotificationBar: React.FC<NotificationBarProps> = ({ vehicles }) => {
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [loading, setLoading] = useState(false);

  // Lade Schadensmeldungen beim Mount
  useEffect(() => {
    const loadDamageReports = async () => {
      try {
        setLoading(true);
        const response = await baserowApi.getDamageReports();
        setDamageReports(response.results);
      } catch (error) {
        console.error('Fehler beim Laden der Schadensmeldungen:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDamageReports();
  }, []);

  const getNotifications = (): Notification[] => {
    const notifications: Notification[] = [];
    const today = new Date();
    
    // Für jedes Fahrzeug separate Benachrichtigungen erstellen
    vehicles.forEach(vehicle => {
      // TÜV Ablauf (30 Tage vorher)
      if (vehicle.tuev_until) {
        const tuvDate = new Date(vehicle.tuev_until);
        const daysUntilTuv = Math.ceil((tuvDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (daysUntilTuv <= 30 && daysUntilTuv > 0) {
          notifications.push({
            type: 'warning',
            message: `TÜV läuft in ${daysUntilTuv} Tagen ab: ${vehicle.model} (${vehicle.license_plate})`,
            count: 1,
            icon: <AlertTriangle className="w-4 h-4" />
          });
        }
      }

      // Leasing Verträge (6 Monate vorher)
      if (vehicle.end_of_contract) {
        const contractType = typeof vehicle.type_of_contract === 'string' 
          ? vehicle.type_of_contract 
          : vehicle.type_of_contract?.value || '';
        if (contractType === 'Leasing') {
          const contractDate = new Date(vehicle.end_of_contract);
          const monthsUntilContract = Math.ceil((contractDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
          if (monthsUntilContract <= 6 && monthsUntilContract > 0) {
            notifications.push({
              type: 'error',
              message: `Leasing Vertrag endet in ${monthsUntilContract} Monaten: ${vehicle.model} (${vehicle.license_plate})`,
              count: 1,
              icon: <Calendar className="w-4 h-4" />
            });
          }
        }
      }

      // Andere Verträge (6 Monate vorher)
      if (vehicle.end_of_contract) {
        const contractType = typeof vehicle.type_of_contract === 'string' 
          ? vehicle.type_of_contract 
          : vehicle.type_of_contract?.value || '';
        if (contractType && contractType !== 'Leasing' && contractType !== 'Barzahlung') {
          const contractDate = new Date(vehicle.end_of_contract);
          const monthsUntilContract = Math.ceil((contractDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
          if (monthsUntilContract <= 6 && monthsUntilContract > 0) {
            notifications.push({
              type: 'warning',
              message: `Vertrag endet in ${monthsUntilContract} Monaten: ${vehicle.model} (${vehicle.license_plate})`,
              count: 1,
              icon: <Clock className="w-4 h-4" />
            });
          }
        }
      }
    });

    // Projekte mit Status "Gemeldet" einzeln hinzufügen
    const reportedProjects = damageReports.filter(report => 
      report.status.value === 'Gemeldet'
    );

    // Für jedes gemeldete Projekt eine separate Benachrichtigung erstellen
    reportedProjects.forEach(project => {
      // Extrahiere Kategorie aus Beschreibung
      const category = extractCategoryFromDescription(project.description || '');
      
      // Nur Projekte mit gültiger Kategorie berücksichtigen
      if (!category) {
        return; // Projekte ohne gültige Kategorie ignorieren
      }
      
      // Finde das betroffene Fahrzeug
      const affectedVehicle = vehicles.find(vehicle => 
        project.carpool && project.carpool.some(car => car.id === vehicle.id)
      );
      
      const vehicleInfo = affectedVehicle 
        ? `${affectedVehicle.model} (${affectedVehicle.license_plate})`
        : project.carpool && project.carpool.length > 0 
          ? `Fahrzeug-ID: ${project.carpool[0].id}`
          : 'Kein Fahrzeug zugeordnet';

      // Bestimme Nachrichtentyp und Icon basierend auf Kategorie
      let notificationType: 'warning' | 'error' | 'info' = 'info';
      let categoryIcon = <AlertCircle className="w-4 h-4" />;
      let categoryLabel = 'Projekt';

      switch (category) {
        case 'Schaden':
          notificationType = 'error';
          categoryIcon = <AlertTriangle className="w-4 h-4" />;
          categoryLabel = 'Schadensmeldung';
          break;
        case 'Termin':
          notificationType = 'info';
          categoryIcon = <Calendar className="w-4 h-4" />;
          categoryLabel = 'Termin';
          break;
        case 'Service & Inspektion':
          notificationType = 'warning';
          categoryIcon = <Clock className="w-4 h-4" />;
          categoryLabel = 'Service & Inspektion';
          break;
      }

      notifications.push({
        type: notificationType,
        message: `${categoryLabel}: "${project.title}" - Fahrzeug: ${vehicleInfo}`,
        count: 1,
        icon: categoryIcon
      });
    });

    return notifications;
  };

  const notifications = getNotifications();

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="mb-6 space-y-3">
      {notifications.map((notification, index) => (
        <div
          key={index}
          className={`p-4 rounded-lg border-l-4 flex items-center justify-between ${
            notification.type === 'error'
              ? 'bg-red-50 border-red-400 text-red-800'
              : notification.type === 'warning'
              ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
              : 'bg-blue-50 border-blue-400 text-blue-800'
          }`}
        >
          <div className="flex items-center gap-3">
            {notification.icon}
            <span className="font-medium">{notification.message}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Anzahl wird nicht mehr angezeigt, da jedes Fahrzeug eine eigene Benachrichtigung hat */}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationBar; 